import classNames from 'classnames';
import React, { useMemo } from 'react';
import { ButtonSize } from '../../interfaces/enums/ButtonSize.enum';
import { ButtonType } from '../../interfaces/enums/ButtonType.enum';
import CustomLink from '../CustomLink/CustomLink';

export interface ButtonLinkProps {
  url?: string;
  label: string;
  children: React.ReactNode;
  size: ButtonSize;
  type: ButtonType;
  isOnDowntimeBanner?: boolean
  className?: string;
  onMouseEnter?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  onMouseLeave?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  onClick?: (() => void) | ((e: React.MouseEvent<HTMLElement>) => void);
  btnType?: 'button' | 'submit' | 'reset' | undefined;
}

const ButtonLink: React.FC<ButtonLinkProps> = ({
  url = undefined,
  label,
  children,
  type = ButtonType.PRIMARY,
  size = ButtonSize.LG,
  isOnDowntimeBanner = false,
  className,
  onMouseEnter,
  onMouseLeave,
  onClick,
  btnType,
}) => {
  const buttonType = useMemo<string>(() => {
    switch (type) {
      case ButtonType.PRIMARY:
        return ' border-button/primary-bg hover:border-button/primary-bg-hover bg-button/primary-bg hover:bg-button/primary-bg-hover text-button/primary-label';
      case ButtonType.SECONDARY:
        return ' border-button/secondary-bg hover:border-button/secondary-bg-hover bg-button/secondary-bg hover:bg-button/secondary-bg-hover text-button/primary-label';
      case ButtonType.TERTIARY:
        return ' border-button/outline-border hover:border-button/outline-bg-hover text-button/outline-label hover:text-button/outline-label-hover bg-transparent hover:bg-button/outline-bg-hover';
    }
  }, [type]);

  const buttonSize = useMemo<string>(() => {
    switch (size) {
      case ButtonSize.SM:
        return ' py-1 px-3';
      case ButtonSize.MD:
        return ' py-2 px-6';
      case ButtonSize.LG:
        return ' h-12 py-3 px-8';
      case ButtonSize.XL:
        return ' h-12 py-4 px-8';
    }
  }, [size]);

  const BUTTON_LINK_CLASSNAMES = useMemo<string>(() => {
    return classNames(
      `group inline-flex justify-center items-center leading-none transition-all ease-in-out duration-300 cursor-pointer focus:ring-0 focus:outline-none fit-content uppercase rounded-full font-bold border no-underline text-base`,
      buttonType,
      buttonSize,
      className
    );
  }, [type, size, className]);

  const ConditionalWrapper = (props: {
    url: string | undefined;
    children: any;
    onMouseEnter: React.MouseEventHandler<HTMLButtonElement> | undefined;
    onMouseLeave: React.MouseEventHandler<HTMLButtonElement> | undefined;
    onClick: React.MouseEventHandler<HTMLButtonElement> | undefined;
  }): JSX.Element => {
    return props.url ? (
      <CustomLink
        url={props.url}
        label={label}
        className={BUTTON_LINK_CLASSNAMES}
        onClick={onClick}
      >
        {props.children}
      </CustomLink>
    ) : (
      <button
        onClick={props.onClick}
        onMouseEnter={props.onMouseEnter}
        onMouseLeave={props.onMouseLeave}
        className={BUTTON_LINK_CLASSNAMES}
        type={btnType || undefined}
      >
        {props.children}
      </button>
    );
  };

  return (
    <ConditionalWrapper
      url={url}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </ConditionalWrapper>
  );
};

export default ButtonLink;
